/**
 * Invite a friend (plan.md step 89, the user, 2026-10-29): the invite the screen shows as a QR
 * code and shares — asked of `POST /public/invites` and KEPT on the device, so the screen opens
 * at the rave with no signal. Per rave (`myrave:{editionId}:invite`) or for MyRave
 * (`myrave:platform:invite`), and per inviter (`owner`): a code kept for one account is never
 * shown for the next one, or for a guest.
 *
 * Signed out the inviter is this DEVICE — its random id (`inviteClientId`, the one invite
 * tracking already keeps). After a sign-in the device's guest invites become the account's
 * (`adoptGuestInvites`), and the guest "generation" moves on, so a guest code kept from before
 * is never shown again as a guest's.
 */
import { withInviteVia, type InviteLink, type InviteVia } from '@myrave/shared';
import { api } from './api';
import { inviteClientId } from './inviteTracking';
import { readJson, writeJson } from './storage';

const KEY = 'invite';
/** `{ made: boolean, gen: number }` — whether this device made guest invites, and how many times they were adopted. */
const GUEST_KEY = 'guest-invites';

export interface KeptInvite extends InviteLink {
  owner: string;
  /** The QR code's drawing for this code, once drawn — kept so it shows offline. */
  qr?: string | null;
  /** The rave logo drawn inside that QR (a rave's invite, 2026-10-31) — a drawing made without it, or with another, is drawn again. */
  qrLogo?: string | null;
}

interface GuestState { made: boolean; gen: number }
const guestState = (): GuestState => {
  const g = readJson<GuestState | null>(null, GUEST_KEY, null);
  return g && typeof g.gen === 'number' ? { made: Boolean(g.made), gen: g.gen } : { made: false, gen: 0 };
};

/** Whose invite this device shows: the account's, or this generation of the guest's. */
export const inviteOwner = (userId: string | null | undefined): string => userId ?? `guest:${guestState().gen}`;

const isGuest = (owner: string) => owner.startsWith('guest:');

/** The invite kept for this rave (or MyRave) and this inviter — null for anybody else's. */
export function keptInvite(editionId: string | null, owner: string): KeptInvite | null {
  const k = readJson<KeptInvite | null>(editionId, KEY, null);
  return k && k.owner === owner && typeof k.code === 'string' && typeof k.url === 'string' ? k : null;
}

/** Ask the server (the same code every time), and keep it. */
export async function fetchInvite(editionId: string | null, owner: string): Promise<KeptInvite> {
  const guest = isGuest(owner);
  const link = await api.post<InviteLink>('/public/invites', {
    ...(editionId ? { editionId } : {}),
    ...(guest ? { clientId: inviteClientId() } : {}),
  });
  const before = keptInvite(editionId, owner);
  // The drawing AND the logo it was drawn with stay (0.14.76 walk: dropping the logo had the next open draw it again).
  const same = before?.url === link.url;
  const kept: KeptInvite = { ...link, owner, qr: same ? before.qr ?? null : null, qrLogo: same ? before.qrLogo ?? null : null };
  writeJson(editionId, KEY, kept);
  if (guest) writeJson(null, GUEST_KEY, { ...guestState(), made: true } satisfies GuestState);
  return kept;
}

/** Keep the QR code's drawing beside the invite it encodes. */
export function keepInviteQr(editionId: string | null, invite: KeptInvite, svg: string, logo: string | null = null): void {
  const now = keptInvite(editionId, invite.owner);
  if (now && now.url === invite.url) writeJson(editionId, KEY, { ...now, qr: svg, qrLogo: logo });
}

/**
 * Signed in on a device that made guest invites: they become the account's — once. Never
 * awaited by a screen; a failure (no signal) tries again at the next sign-in or load.
 */
export async function adoptGuestInvites(): Promise<void> {
  const g = guestState();
  if (!g.made) return;
  try {
    await api.post('/public/invites/adopt', { clientId: inviteClientId() });
    writeJson(null, GUEST_KEY, { made: false, gen: g.gen + 1 } satisfies GuestState);
  } catch {
    // Kept for the next time.
  }
}

/** The link as each way shares it — the board counts how it was shared. */
export const inviteLinkVia = (invite: InviteLink, via: InviteVia): string => withInviteVia(invite.url, via);

/**
 * The drawing of a link as a QR code in MyRave's design — checked by reading it back. A rave's invite carries the
 * rave's logo inside it, as its tickets do (the user, 2026-10-31); a logo that makes it unreadable leaves MyRave's alone.
 */
export async function drawInviteQr(url: string, logoUrl: string | null): Promise<string | null> {
  const codes = await import('./lazy/ticketCodes');
  const rave = logoUrl ? await codes.raveLogoOf(logoUrl).catch(() => null) : null;
  return (await codes.drawCode({ format: 'QRCode', text: url, binary: false }, rave)).svg;
}

/**
 * Ready for no signal (the 0.14.76 walk: "the QR code inside an offline rave should work without opening the invite
 * first online"): when a rave is kept for offline, its invite is asked for and its QR drawn and kept now — so the
 * first open at the rave, with no signal, shows the code. Never awaited by a screen; a failure tries next load.
 */
export async function warmInvite(editionId: string | null, owner: string, logoUrl: string | null): Promise<void> {
  try {
    const invite = await fetchInvite(editionId, owner);
    if (invite.qr && (invite.qrLogo ?? null) === logoUrl) return;
    const svg = await drawInviteQr(inviteLinkVia(invite, 'qr'), logoUrl);
    if (svg) keepInviteQr(editionId, invite, svg, logoUrl);
  } catch {
    // No signal or refused — the next load of the rave tries again.
  }
}
